import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Send, Trash2, FileSpreadsheet } from "lucide-react";
import { API } from "../../apiBase";

const SESAM = { standard: "SESAM std", sans_vitale: "Sans Vitale", degrade: "Dégradé" };

function Row({ inv, mandatEmail, onDeleted, onTransmitted }) {
  const [busy, setBusy] = useState(false);
  const exon = inv.act_type === "nomenclature";

  const download = async () => {
    try {
      const r = await axios.get(`${API}/pro/facturation/invoices/${inv.id}/facturx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Téléchargement impossible."); }
  };

  const transmit = async () => {
    if (!mandatEmail) { toast.error("Configurez d'abord le mandat expert-comptable."); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/pro/facturation/invoices/${inv.id}/transmit`, {});
      toast.success(`Facture transmise à ${r.data.to}.`);
      onTransmitted(inv.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transmission impossible.");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    try {
      await axios.delete(`${API}/pro/facturation/invoices/${inv.id}`);
      onDeleted(inv.id);
    } catch { toast.error("Suppression impossible."); }
  };

  return (
    <tr className="border-t border-[#E2E8F0]" data-testid={`inv-row-${inv.id}`}>
      <td className="py-2.5 pr-3 text-[12.5px] font-medium text-[#0F2B5B] whitespace-nowrap">{inv.number}</td>
      <td className="py-2.5 pr-3 text-[12px] text-[#475569] whitespace-nowrap">{inv.date}</td>
      <td className="py-2.5 pr-3 text-[12.5px] text-[#0F2B5B] max-w-[160px] truncate">{inv.client_name}</td>
      <td className="py-2.5 pr-3">
        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
          exon ? "bg-[#ECFDF5] text-[#059669]" : "bg-[#FEF3C7] text-[#B45309]"}`}>
          {exon ? "Exonéré 261-4" : `TVA ${inv.tva_rate} %`}
        </span>
      </td>
      <td className="py-2.5 pr-3 text-[11px] text-[#475569] whitespace-nowrap">{SESAM[inv.sesam_mode] || "—"}</td>
      <td className="py-2.5 pr-3 text-[12.5px] font-semibold text-[#0F2B5B] whitespace-nowrap">{inv.amount_ttc.toFixed(2)} €</td>
      <td className="py-2.5 pr-3">
        <span className={`text-[10.5px] font-medium ${inv.transmitted ? "text-[#059669]" : "text-[#94A3B8]"}`}>
          {inv.transmitted ? "Transmise" : "Non transmise"}
        </span>
      </td>
      <td className="py-2.5">
        <div className="flex items-center gap-1.5 justify-end">
          <button onClick={download} title="Télécharger Factur-X" data-testid={`inv-download-${inv.id}`}
                  className="grid place-items-center h-7 w-7 rounded-full border border-[#E2E8F0] text-[#0F2B5B] hover:border-[#0EA5E9]">
            <Download size={12} /></button>
          <button onClick={transmit} disabled={busy} title="Transmettre au comptable" data-testid={`inv-transmit-${inv.id}`}
                  className="grid place-items-center h-7 w-7 rounded-full border border-[#E2E8F0] text-[#0284C7] hover:border-[#0EA5E9] disabled:opacity-40">
            <Send size={12} /></button>
          <button onClick={remove} title="Supprimer" data-testid={`inv-delete-${inv.id}`}
                  className="grid place-items-center h-7 w-7 rounded-full text-[#DC2626] hover:bg-[#FEF2F2]">
            <Trash2 size={12} /></button>
        </div>
      </td>
    </tr>
  );
}

export default function InvoiceList({ invoices, setInvoices, mandatEmail, year }) {
  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/pro/facturation/export?year=${year}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `e-reporting-${year}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible."); }
  };

  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid="invoice-list">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[13.5px] font-semibold text-[#0F2B5B]">Factures {year}</div>
        <button onClick={exportCsv} data-testid="inv-export-csv"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E2E8F0] text-[12px] text-[#0F2B5B] hover:border-[#0EA5E9]">
          <FileSpreadsheet size={12} /> Export e-reporting (CSV)
        </button>
      </div>
      {invoices.length === 0 ? (
        <p className="text-[12.5px] text-[#475569] py-4">Aucune facture émise cette année.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-[10.5px] uppercase tracking-[0.12em] text-[#475569]">
                <th className="pb-2 pr-3">N°</th><th className="pb-2 pr-3">Date</th>
                <th className="pb-2 pr-3">Client</th><th className="pb-2 pr-3">Régime</th>
                <th className="pb-2 pr-3">SESAM</th><th className="pb-2 pr-3">TTC</th>
                <th className="pb-2 pr-3">Comptable</th><th className="pb-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <Row key={inv.id} inv={inv} mandatEmail={mandatEmail}
                     onDeleted={(id) => setInvoices((p) => p.filter((x) => x.id !== id))}
                     onTransmitted={(id) => setInvoices((p) => p.map((x) => x.id === id ? { ...x, transmitted: true } : x))} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
